import React, { createContext, useContext, useEffect, useReducer } from 'react';
import type { ReactNode } from 'react';
import { apiConfig } from '../config/api';

export type Environment = 'dev' | 'prod';

export interface AppState {
  selectedEnvironment: Environment;
  debugMode: boolean;
}

export interface AppContextType {
  appState: AppState;
  setEnvironment: (env: Environment) => void;
  debugMode: boolean;
  setDebugMode: (enabled: boolean) => void;
  toggleDebugMode: () => void;
}

type AppAction =
  | { type: 'SET_ENVIRONMENT'; payload: Environment }
  | { type: 'SET_DEBUG_MODE'; payload: boolean }
  | { type: 'TOGGLE_DEBUG_MODE' }
  | { type: 'LOAD_STATE'; payload: Partial<AppState> };

const getInitialEnvironment = (): Environment =>
  (apiConfig.environment.toLowerCase() === 'prod' ? 'prod' : 'dev');

const initialState: AppState = {
  selectedEnvironment: getInitialEnvironment(),
  debugMode: false,
};

function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ENVIRONMENT':
      return { ...state, selectedEnvironment: action.payload };
    case 'SET_DEBUG_MODE':
      return { ...state, debugMode: action.payload };
    case 'TOGGLE_DEBUG_MODE':
      return { ...state, debugMode: !state.debugMode };
    case 'LOAD_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const saved = localStorage.getItem('trapi-ui-app');
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Partial<AppState>;
        dispatch({ type: 'LOAD_STATE', payload: parsed });
      } catch {}
    }
  }, []);

  // Migration: if build-time environment is prod but a stale persisted dev value exists, correct it.
  useEffect(() => {
    if (apiConfig.environment === 'prod' && state.selectedEnvironment !== 'prod') {
      dispatch({ type: 'SET_ENVIRONMENT', payload: 'prod' });
    }
  }, [state.selectedEnvironment]);

  useEffect(() => {
    localStorage.setItem(
      'trapi-ui-app',
      JSON.stringify({ selectedEnvironment: state.selectedEnvironment, debugMode: state.debugMode })
    );
  }, [state.selectedEnvironment, state.debugMode]);

  const setEnvironment = (env: Environment) => dispatch({ type: 'SET_ENVIRONMENT', payload: env });
  const setDebugMode = (enabled: boolean) => dispatch({ type: 'SET_DEBUG_MODE', payload: enabled });
  const toggleDebugMode = () => dispatch({ type: 'TOGGLE_DEBUG_MODE' });

  const value: AppContextType = {
    appState: state,
    setEnvironment,
    debugMode: state.debugMode,
    setDebugMode,
    toggleDebugMode,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

export const useEnvironment = () => {
  const { appState, setEnvironment } = useApp();
  return {
    currentEnvironment: appState.selectedEnvironment,
    setEnvironment,
    isDev: appState.selectedEnvironment === 'dev',
    isProd: appState.selectedEnvironment === 'prod',
  };
};
